import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { Scatterplot } from '@/components/scatterplot';
import { countryIdToName } from '@/utils/country-id-to-name';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type EqualityForSomeChapter2Props = {
  data: EqualityForSomeData['lgbtq_equality_country_bd'];
};

export function EqualityForSomeChapter2(props: EqualityForSomeChapter2Props) {
  const { data } = props;

  const { i18n } = useLingui();

  const chartData = (Object.entries(data) as [CountryIsoCode, AgeGroupBreakdown][]).map(
    ([key, value]) => ({
      id: key,
      label: countryIdToName(key),
      ...value,
    })
  );

  const getChartLabel = (generation: string) =>
    t({
      id: 'equality-for-some.chapter-2.chart-label',
      message: `More ${generation} say equal treatment of LGBTQ+ people is very important`,
    });

  const [highlightedCountries, setHighlightedCountries] = useState<CountryIsoCode[]>();

  const handleStepEnter = async (evt: OnStepEnterExitArgs<CountryIsoCode[] | undefined>) => {
    setHighlightedCountries(evt.data);
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <Scatterplot
          data={chartData}
          highlightedIds={highlightedCountries}
          getXValue={(d) => d.older}
          getYValue={(d) => d.younger}
          bottomAxisLabel={getChartLabel(olderGeneration(i18n))}
          topAxisLabel={getChartLabel(youngerGeneration(i18n))}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter}>
        <ScrollStep>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-0">
              While attitudes about discrimination against women and against ethnic, racial and
              religious minorities don’t diverge clearly between generations, they do when it comes
              to the <PrimaryAnnotatedType>rights of LGBTQ+ people.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>
        <ScrollStep>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-1">
              Here we see young people expressing significantly more concern for equal treatment
              than older people across nearly{' '}
              <PrimaryAnnotatedType>all surveyed countries</PrimaryAnnotatedType> - and in the
              process, driving positive change.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-2">
              <AnnotatedType>Large generational differences</AnnotatedType> are apparent in a
              diverse range of countries&hellip;
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['JP']}>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-3">
              &hellip;from{' '}
              <PrimaryAnnotatedType>{countryIdToName('JP')}&hellip;</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['ES']}>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-4">
              &hellip;to{' '}
              <PrimaryAnnotatedType>{countryIdToName('ES')}&hellip;</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['KE']}>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-5">
              &hellip;to{' '}
              <PrimaryAnnotatedType>{countryIdToName('KE')}&hellip;</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={['PE']}>
          <StoryText>
            <Trans id="equality-for-some.chapter-2.text-6">
              &hellip;to <PrimaryAnnotatedType>{countryIdToName('PE')}.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
